import {HttpClient, HttpResponse} from '@angular/common/http';
import {Inject, Injectable, makeStateKey, PLATFORM_ID, StateKey, TransferState} from '@angular/core';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {ApiHelper} from './api.helper';
import {WPPostBlog} from '../types/WPPostBlog';
import {WPComment} from '../types/WPComment';
import {ToolsService} from './tools.service';
import {AppConfigService} from './app-config.service';
import {TransferStateKeyEnum} from '../enums/TransferStateKeyEnum';
import {isPlatformBrowser, isPlatformServer} from '@angular/common';

@Injectable()
export class StaticBlogService {
  constructor(
    private http: HttpClient,
    private apiHelper: ApiHelper,
    private tools: ToolsService,
    private appConfigService: AppConfigService,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {}

  public GetBlogLatest(page: number, pageSize: number, excludedIds?: number[]): Observable<WPPostBlog[]> {
    const apiUrl: string = this.appConfigService.appConfig.amazonS3Url;
    const endpoint = `${apiUrl}wp/blog/blog-page-${page}.json`;

    return this.http.get<WPPostBlog[]>(endpoint).pipe(
      map((posts) => {
        let filteredPosts: WPPostBlog[] = posts;
        if (!this.tools.isNil(excludedIds) && excludedIds.length > 0) {
          filteredPosts = posts.filter((post) => !excludedIds.includes(post.id));
        }
        if (filteredPosts.length > pageSize) {
          filteredPosts = filteredPosts.slice(0, pageSize);
        }
        return filteredPosts;
      }),
      catchError((error) => this.apiHelper.handleError(error)),
    );
  }

  public GetBlogLatestResponse(
    page: number,
    tag?: number,
  ): Observable<HttpResponse<WPPostBlog[]> & {totalPages: number; total: number}> {
    const apiUrl: string = this.appConfigService.appConfig.amazonS3Url;
    let endpoint: string;

    if (tag != null) {
      endpoint = `${apiUrl}wp/blog/blogtag-${tag}-page-${page}.json`;
    } else {
      endpoint = `${apiUrl}wp/blog/blog-page-${page}.json`;
    }

    const stateKey: StateKey<{totalPages: number; total: number}> = makeStateKey(
      `${TransferStateKeyEnum.NATIVE_RESPONSE_DATA}-${tag}`,
    );

    if (isPlatformBrowser(this.platformId)) {
      const cachedData = this.transferState.get(stateKey, null);
      if (cachedData) {
        this.transferState.remove(stateKey);

        return this.http.get<WPPostBlog[]>(endpoint).pipe(
          map((posts) => {
            let filteredPosts = [...posts];

            return {
              body: filteredPosts,
              totalPages: cachedData.totalPages,
              total: cachedData.total,
            } as HttpResponse<WPPostBlog[]> & {totalPages: number; total: number};
          }),
          catchError((error) => this.apiHelper.handleError(error)),
        );
      }
    }

    let totalPages = 1;
    let total = 0;

    return this.http.get<WPPostBlog[]>(endpoint, {observe: 'response'}).pipe(
      map((response) => {
        const totalPagesHeader = response.headers.get('x-amz-meta-x-total-pages');
        if (totalPagesHeader) {
          totalPages = parseInt(totalPagesHeader, 10);
        }

        const totalHeader = response.headers.get('x-amz-meta-x-total');
        if (totalHeader) {
          total = parseInt(totalHeader, 10);
        }

        if (isPlatformServer(this.platformId)) {
          this.transferState.set(stateKey, {totalPages, total});
        }

        const enhancedResponse = response as HttpResponse<WPPostBlog[]> & {totalPages: number; total: number};
        enhancedResponse.totalPages = totalPages;
        enhancedResponse.total = total;

        return enhancedResponse;
      }),
      catchError((error) => this.apiHelper.handleError(error)),
    );
  }

  public GetBlogCommentsLatestResponse(
    page: number,
    postId?: number,
  ): Observable<HttpResponse<WPComment[]> & {totalPages: number; total: number}> {
    const apiUrl: string = this.appConfigService.appConfig.amazonS3Url;
    let endpoint: string;

    if (postId == null) {
      return of(null);
    }
    endpoint = `${apiUrl}wp/blog/blog-comments-${postId}-page-${page}.json`;

    let totalPages = 1;
    let total = 0;

    return this.http.get<WPComment[]>(endpoint, {observe: 'response'}).pipe(
      map((response) => {
        const totalPagesHeader = response.headers.get('x-amz-meta-x-total-pages');
        if (totalPagesHeader) {
          totalPages = parseInt(totalPagesHeader, 10);
        }

        const totalHeader = response.headers.get('x-amz-meta-x-total');
        if (totalHeader) {
          total = parseInt(totalHeader, 10);
        }

        const enhancedResponse = response as HttpResponse<WPComment[]> & {totalPages: number; total: number};
        enhancedResponse.totalPages = totalPages;
        enhancedResponse.total = total;

        return enhancedResponse;
      }),
      catchError((error) => this.apiHelper.handleError(error)),
    );
  }

  public GetBlogByTag(page: number, tags: number[], excludedIds?: number[]): Observable<WPPostBlog[]> {
    const apiUrl: string = this.appConfigService.appConfig.amazonS3Url;
    // Since we can only query one tag at a time with static files, we'll use the first tag
    const tag = tags[0];
    const endpoint = `${apiUrl}wp/blog/blogtag-${tag}-page-${page}.json`;

    return this.http.get<WPPostBlog[]>(endpoint).pipe(
      map((posts) => {
        if (!this.tools.isNil(excludedIds) && excludedIds.length > 0) {
          return posts.filter((post) => !excludedIds.includes(post.id));
        }
        return posts;
      }),
      catchError((error) => this.apiHelper.handleError(error)),
    );
  }

  public GetBlogPostById(id: number): Observable<WPPostBlog> {
    const apiUrl: string = this.appConfigService.appConfig.amazonS3Url;
    const endpoint = `${apiUrl}wp/blog/blog-${id}.json`;

    return this.http.get<WPPostBlog>(endpoint).pipe(catchError((error) => this.apiHelper.handleError(error)));
  }

  public GetBlogByCategories(
    cpruTypes: number[],
    serviceTypes: number[],
    places: number[],
    excludedIds?: number[],
  ): Observable<WPPostBlog[]> {
    const apiUrl: string = this.appConfigService.appConfig.amazonS3Url;
    const observables: Observable<any>[] = [];

    // Load blog content for cpruTypes
    if (!this.tools.isNil(cpruTypes) && cpruTypes.length > 0) {
      cpruTypes.forEach((id) => {
        observables.push(
          this.http
            .get<WPPostBlog[]>(`${apiUrl}wp/related/related-blog-care_provider_unit_type-${id}.json`)
            .pipe(catchError((error) => of([]))),
        );
      });
    }

    // Load blog content for serviceTypes
    if (!this.tools.isNil(serviceTypes) && serviceTypes.length > 0) {
      serviceTypes.forEach((id) => {
        observables.push(
          this.http
            .get<WPPostBlog[]>(`${apiUrl}wp/related/related-blog-care_service_type-${id}.json`)
            .pipe(catchError((error) => of([]))),
        );
      });
    }

    // Load blog content for places (symptoms)
    if (!this.tools.isNil(places) && places.length > 0) {
      places.forEach((id) => {
        observables.push(
          this.http
            .get<WPPostBlog[]>(`${apiUrl}wp/related/related-blog-symptom-${id}.json`)
            .pipe(catchError((error) => of([]))),
        );
      });
    }

    // If no observables were created, return empty array
    if (observables.length === 0) {
      return of([]);
    }

    // Execute all observables and process the results
    return forkJoin(observables).pipe(
      map((results) => {
        // Flatten the array of arrays
        let allPosts: WPPostBlog[] = [];
        results.forEach((posts) => {
          if (posts && posts.length > 0) {
            allPosts = [...allPosts, ...posts];
          }
        });

        // Filter out duplicates by ID
        const uniquePosts = allPosts.filter((post, index, self) => index === self.findIndex((p) => p.id === post.id));

        // Filter out excluded IDs if provided
        if (!this.tools.isNil(excludedIds) && excludedIds.length > 0) {
          return uniquePosts.filter((post) => !excludedIds.includes(post.id));
        }

        return uniquePosts;
      }),
      catchError((error) => this.apiHelper.handleError(error)),
    );
  }
}
